<script setup lang="ts">
// /indoor-air-quality — mockup page 6 (M8).
//
// Same four solutions as the mockup, each carrying the caveat the brochure
// leaves out, plus a matcher (askhvac.iaq_match) whose job is partly to
// talk people out of buying. Two of its paths recommend no product at all:
// a damp house is a humidity problem, and a burning smell is a safety call.
import { ref } from 'vue'
import { RouterLink } from 'vue-router'

import TreeWalker from '@/shared/components/TreeWalker.vue'
import type { Answer, DecisionTree, TreeNode } from '@/shared/diagnostic/types'
import treeJson from '@contracts/decision-tree.askhvac-iaq-match.v1.json'
import { contactContext, contactContextLabel } from '../store'
import { BENEFITS, EPA_FACTS, SOLUTIONS } from '../content/iaq'
// Pixabay Content License — see src/assets/ATTRIBUTIONS.md. Borrowed from
// the ror brand until the redesign's own photography is sourced.
import heroPhoto from '@/assets/ror-comfort.jpg'

const tree = treeJson as unknown as DecisionTree

const matching = ref(false)
const walker = ref<InstanceType<typeof TreeWalker> | null>(null)
const outcome = ref<TreeNode | null>(null)

function startMatch() {
    matching.value = true
    outcome.value = null
    walker.value?.reset()
    requestAnimationFrame(() =>
        document.getElementById('match')?.scrollIntoView({ behavior: 'smooth', block: 'center' }),
    )
}

function onOutcome(_id: string, node: TreeNode, answers: Answer[]) {
    outcome.value = node
    contactContext.value = { tree_id: tree.id, answers }
    contactContextLabel.value = tree.title ?? tree.id
    // Deliberately no lead intent: wanting cleaner air says nothing about
    // repairing or replacing the system, so the backend classifies from the
    // evidence rather than being told something the visitor never said.
}

function restartMatch() {
    outcome.value = null
    walker.value?.reset()
}

const HERO_BADGES = [
    'Fewer allergens',
    'Less dust',
    'Odour control',
    'A cleaner coil',
]

const PROMISE = [
    'What each product actually does',
    'What it does not do',
    'Which one matches your complaint',
    'When the answer is to buy nothing',
]
</script>

<template>
    <section class="hero" :style="{ backgroundImage: `url(${heroPhoto})` }">
        <div class="wash" aria-hidden="true"></div>
        <div class="hero-inner">
            <div>
                <p class="crumb">Home <span>›</span> Indoor Air Quality</p>
                <h1>Breathe Cleaner,<br /><span class="hl">Healthier Air</span> at Home</h1>
                <p class="lede">
                    The air-quality aisle is full of devices sold on numbers nobody can trace.
                    Here's what each one genuinely does, what it doesn't, and how to tell which —
                    if any — is worth your money.
                </p>
                <ul class="badges">
                    <li v-for="b in HERO_BADGES" :key="b">{{ b }}</li>
                </ul>
            </div>

            <aside class="promise">
                <h2>What this page will tell you</h2>
                <ul>
                    <li v-for="p in PROMISE" :key="p">{{ p }}</li>
                </ul>
                <p class="note">
                    We don't sell or install any of it. That's the whole reason we can say the last
                    one out loud.
                </p>
            </aside>
        </div>
    </section>

    <section class="solutions">
        <div class="solutions-inner">
            <h2 class="centered">The four things you'll be offered</h2>
            <p class="sub centered">
                In roughly the order we'd consider them for a typical Florida home.
            </p>

            <div class="cards">
                <article v-for="s in SOLUTIONS" :key="s.id" class="sol">
                    <span class="chip">
                        <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" v-html="s.icon"></svg>
                    </span>
                    <h3>{{ s.name }}</h3>
                    <p class="summary">{{ s.summary }}</p>
                    <ul>
                        <li v-for="p in s.points" :key="p">{{ p }}</li>
                    </ul>
                    <p class="caveat">
                        <strong>The catch:</strong>
                        {{ s.caveat }}
                    </p>
                </article>
            </div>
        </div>
    </section>

    <section class="facts">
        <div class="facts-inner">
            <div class="facts-copy">
                <h2>Breathe easier. Live better.</h2>
                <p>
                    Indoor air quality is worth taking seriously — most of a Florida year is spent
                    inside with the windows shut. That's the case for doing something. It isn't a
                    case for buying everything.
                </p>
                <div class="benefit-grid">
                    <div v-for="b in BENEFITS" :key="b.title" class="benefit">
                        <span class="chip">
                            <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" v-html="b.icon"></svg>
                        </span>
                        <strong>{{ b.title }}</strong>
                        <p>{{ b.body }}</p>
                    </div>
                </div>
            </div>

            <aside class="stats">
                <div v-for="f in EPA_FACTS" :key="f.stat" class="stat">
                    <strong>{{ f.stat }}</strong>
                    <p>{{ f.claim }}</p>
                    <small>{{ f.source }}</small>
                </div>
                <p class="stat-note">
                    These are the only two numbers on this page, and both come from the EPA. We
                    don't print performance percentages we can't point at a test for.
                </p>
            </aside>
        </div>
    </section>

    <section id="match" class="match">
        <div class="match-inner">
            <div class="match-copy">
                <h2>Which one is right for you?</h2>
                <p class="sub">
                    Three questions at most. Two of the answers it can give you are “fix something
                    else first” and “don't buy anything yet” — which is the point.
                </p>

                <div v-if="!matching" class="start">
                    <button type="button" class="cta" @click="startMatch">
                        Find What Actually Fits →
                    </button>
                    <p class="fine">Under a minute. Nothing to fill in.</p>
                </div>

                <div v-else class="walk">
                    <TreeWalker
                        v-show="!outcome"
                        ref="walker"
                        :tree="tree"
                        why-variant="aside"
                        data-use-note="Your answers stay on this page unless you choose to send them to a pro."
                        @outcome="onOutcome"
                    />

                    <div v-if="outcome" class="verdict">
                        <strong>{{ outcome.prompt }}</strong>
                        <p>{{ outcome.body }}</p>
                        <ol v-if="outcome.steps?.length">
                            <li v-for="s in outcome.steps" :key="s">{{ s }}</li>
                        </ol>
                        <button type="button" class="restart" @click="restartMatch">Start over</button>
                    </div>
                </div>
            </div>

            <aside class="want">
                <h3>Want cleaner air?</h3>
                <p>
                    A local pro can measure what's actually in your air and what your ducts can
                    carry, which is the part no website can do.
                </p>
                <ul>
                    <li>No obligation</li>
                    <li>They see your answers, so you repeat nothing</li>
                    <li>Options at every budget, including the cheap one</li>
                </ul>
                <RouterLink class="cta" to="/second-opinion">Get My Free Assessment →</RouterLink>
                <p class="fine">100% free · No obligation · Local experts</p>
            </aside>
        </div>
    </section>
</template>

<style scoped lang="scss">
/* ---- hero ---- */
.hero {
    position: relative;
    background-size: cover;
    background-position: center;
    color: #fff;
}

.wash {
    position: absolute;
    inset: 0;
    background: var(--site-gradient-hero-overlay);
}

.hero-inner {
    position: relative;
    z-index: 1;
    max-width: var(--site-content-max);
    margin: 0 auto;
    padding: var(--site-space-xl) var(--site-space-lg);
    display: grid;
    grid-template-columns: minmax(0, 1fr) 19rem;
    gap: var(--site-space-xl);
    align-items: center;

    @media (max-width: 960px) {
        grid-template-columns: 1fr;
    }
}

.crumb {
    margin: 0 0 var(--site-space-sm);
    font-size: 0.76rem;
    color: rgba(255, 255, 255, 0.62);

    span {
        margin: 0 0.3rem;
    }
}

h1 {
    font-family: var(--site-font-heading);
    font-size: clamp(1.8rem, 3.6vw, 2.7rem);
    font-weight: 800;
    line-height: 1.14;
    margin: 0 0 var(--site-space-md);
    text-shadow: 0 2px 18px rgba(3, 15, 40, 0.45);
}

.hl {
    color: var(--site-accent);
}

.lede {
    max-width: 36rem;
    margin: 0 0 var(--site-space-lg);
    color: rgba(255, 255, 255, 0.88);
    line-height: 1.6;
}

.badges {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-wrap: wrap;
    gap: var(--site-space-md);
    font-size: 0.8rem;
    font-weight: 600;

    li::before {
        content: '✓';
        margin-right: 0.4rem;
        color: #4ade80;
    }
}

.promise {
    background: rgba(7, 28, 69, 0.84);
    border: 1px solid rgba(255, 255, 255, 0.14);
    border-radius: var(--site-radius-md);
    padding: var(--site-space-md);

    h2 {
        font-family: var(--site-font-heading);
        font-size: 0.98rem;
        color: #fff;
        margin: 0 0 var(--site-space-sm);
    }

    ul {
        list-style: none;
        margin: 0 0 var(--site-space-sm);
        padding: 0;
        display: grid;
        gap: 0.45rem;
        font-size: 0.82rem;
    }

    li::before {
        content: '✓';
        margin-right: 0.45rem;
        color: #4ade80;
        font-weight: 700;
    }

    .note {
        margin: 0;
        font-size: 0.74rem;
        color: rgba(255, 255, 255, 0.66);
        line-height: 1.5;
    }
}

/* ---- shared section bits ---- */
h2 {
    font-family: var(--site-font-heading);
    font-size: clamp(1.3rem, 2.4vw, 1.7rem);
    font-weight: 700;
    color: var(--site-navy);
    margin: 0 0 0.4rem;
}

.sub {
    margin: 0 0 var(--site-space-lg);
    color: var(--site-text-muted);
    font-size: 0.92rem;
    line-height: 1.6;
}

.centered {
    text-align: center;
}

.solutions-inner,
.facts-inner,
.match-inner {
    max-width: var(--site-content-max);
    margin: 0 auto;
    padding: var(--site-section-pad) var(--site-space-lg);
}

/* ---- solution cards ---- */
.cards {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: var(--site-space-md);
    align-items: stretch;
}

.sol {
    display: flex;
    flex-direction: column;
    padding: var(--site-space-md);
    background: var(--site-surface);
    border: 1px solid var(--site-border);
    border-radius: var(--site-radius-md);

    .chip {
        display: grid;
        place-items: center;
        width: 2.6rem;
        height: 2.6rem;
        margin-bottom: 0.6rem;
        border-radius: var(--site-radius-sm);
        background: var(--site-blue-soft);
        color: var(--site-blue);

        svg {
            width: 1.35rem;
            height: 1.35rem;
        }
    }

    h3 {
        font-family: var(--site-font-heading);
        font-size: 0.98rem;
        color: var(--site-navy);
        margin: 0 0 0.35rem;
        line-height: 1.3;
    }

    .summary {
        margin: 0 0 var(--site-space-sm);
        font-size: 0.84rem;
        line-height: 1.55;
        color: var(--site-text);
    }

    ul {
        list-style: none;
        margin: 0 0 var(--site-space-md);
        padding: 0;
        display: grid;
        gap: 0.35rem;
        font-size: 0.82rem;
    }

    li::before {
        content: '✓';
        margin-right: 0.45rem;
        color: var(--site-green);
        font-weight: 700;
    }

    .caveat {
        margin: auto 0 0;
        padding: 0.6rem 0.7rem;
        border-left: 3px solid var(--site-accent);
        border-radius: var(--site-radius-sm);
        background: var(--site-accent-soft);
        font-size: 0.8rem;
        line-height: 1.5;

        strong {
            color: var(--site-accent-strong);
        }
    }
}

/* ---- facts band ---- */
.facts {
    background: var(--site-bg-alt);
}

.facts-inner {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 20rem;
    gap: var(--site-space-xl);
    align-items: start;

    @media (max-width: 1000px) {
        grid-template-columns: 1fr;
    }
}

.facts-copy > p {
    margin: 0 0 var(--site-space-lg);
    max-width: 42rem;
    color: var(--site-text-muted);
    line-height: 1.6;
}

.benefit-grid {
    // Four items, two columns: three-up strands the fourth on its own row.
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: var(--site-space-lg);

    @media (max-width: 560px) {
        grid-template-columns: 1fr;
    }
}

.benefit {
    .chip {
        display: grid;
        place-items: center;
        width: 2.5rem;
        height: 2.5rem;
        margin-bottom: 0.5rem;
        border-radius: var(--site-radius-md);
        background: var(--site-surface);
        border: 1px solid var(--site-border);
        color: var(--site-blue);

        svg {
            width: 1.25rem;
            height: 1.25rem;
        }
    }

    strong {
        display: block;
        font-family: var(--site-font-heading);
        font-size: 0.9rem;
        color: var(--site-navy);
        margin-bottom: 0.2rem;
    }

    p {
        margin: 0;
        font-size: 0.8rem;
        color: var(--site-text-muted);
        line-height: 1.55;
    }
}

.stats {
    display: grid;
    gap: var(--site-space-md);
}

.stat {
    padding: var(--site-space-md);
    border-radius: var(--site-radius-md);
    background: var(--site-surface);
    border: 1px solid var(--site-border);

    strong {
        display: block;
        font-family: var(--site-font-heading);
        font-size: 1.9rem;
        font-weight: 800;
        color: var(--site-blue);
        line-height: 1;
    }

    p {
        margin: 0.35rem 0 0.3rem;
        font-size: 0.84rem;
        line-height: 1.5;
    }

    small {
        font-size: 0.72rem;
        color: var(--site-text-muted);
    }
}

.stat-note {
    margin: 0;
    font-size: 0.76rem;
    color: var(--site-text-muted);
    line-height: 1.55;
}

/* ---- matcher ---- */
.match-inner {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 20rem;
    gap: var(--site-space-xl);
    align-items: start;

    @media (max-width: 1000px) {
        grid-template-columns: 1fr;
    }
}

.walk {
    padding: var(--site-space-lg);
    border-radius: var(--site-radius-md);
    background: var(--site-bg-alt);
    border: 1px solid var(--site-border);
}

.verdict {
    strong {
        display: block;
        font-family: var(--site-font-heading);
        font-size: 1.05rem;
        color: var(--site-navy);
        margin-bottom: 0.4rem;
    }

    p {
        margin: 0 0 var(--site-space-md);
        line-height: 1.6;
    }

    ol {
        margin: 0;
        padding-left: 1.2rem;
        display: grid;
        gap: 0.4rem;
        font-size: 0.9rem;
    }
}

.restart {
    margin-top: var(--site-space-md);
    background: none;
    border: none;
    padding: 0;
    font-family: var(--site-font-body);
    font-size: 0.82rem;
    color: var(--site-text-muted);
    cursor: pointer;

    &:hover {
        color: var(--site-blue);
    }
}

.cta {
    display: inline-block;
    padding: 0.75rem 1.4rem;
    border: none;
    border-radius: var(--site-radius-sm);
    background: var(--site-gradient-cta);
    color: #fff;
    font-family: var(--site-font-body);
    font-weight: 700;
    font-size: 0.92rem;
    text-decoration: none;
    cursor: pointer;

    &:hover {
        background: var(--site-accent-strong);
    }

    /* 48px so a primary CTA is never the hardest thing on the page to
       hit. Mobile only — desktop keeps its tighter proportions. */
    @media (max-width: 860px) {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-height: 48px;
    }
}

.fine {
    margin: 0.5rem 0 0;
    font-size: 0.74rem;
    color: var(--site-text-muted);
}

.want {
    padding: var(--site-space-lg);
    border-radius: var(--site-radius-md);
    background: var(--site-gradient-navy);
    color: #fff;

    h3 {
        font-family: var(--site-font-heading);
        font-size: 1.05rem;
        margin: 0 0 0.35rem;
    }

    > p {
        margin: 0 0 var(--site-space-md);
        font-size: 0.85rem;
        color: rgba(255, 255, 255, 0.82);
        line-height: 1.55;
    }

    ul {
        list-style: none;
        margin: 0 0 var(--site-space-md);
        padding: 0;
        display: grid;
        gap: 0.4rem;
        font-size: 0.82rem;
    }

    li::before {
        content: '✓';
        margin-right: 0.45rem;
        color: #4ade80;
        font-weight: 700;
    }

    .cta {
        display: block;
        text-align: center;
    }

    .fine {
        text-align: center;
        color: rgba(255, 255, 255, 0.65);
    }
}
</style>
